/**
 * 认证状态管理（Zustand）。
 *
 * 职责：仅管理认证相关的状态与 localStorage 持久化，不在此发起 HTTP 请求。
 * 真正的登录/刷新网络请求由 T04 的 services（axios 封装）实现，并通过 `setAuthApi`
 * 注入到本 store，从而避免本模块 import 尚不存在的 api 模块导致编译失败。
 *
 * localStorage key：
 * - `relay_token`：访问令牌
 * - `relay_refresh`：刷新令牌
 * - `relay_user`：用户信息（JSON 序列化）
 *
 * @module store/authStore
 */
import { create } from 'zustand';
import type { User, LoginResult } from '@/types';

/** localStorage 键名。 */
const TOKEN_KEY = 'relay_token';
const REFRESH_KEY = 'relay_refresh';
const USER_KEY = 'relay_user';

/**
 * 认证 API 端口。
 *
 * T04 的 `services/auth.ts` 将实现该接口并通过 `setAuthApi` 注入，
 * 使本 store 在调用 `login` / `refresh` 时能完成真实的网络请求。
 */
export interface AuthApiPort {
  /** 邮箱 + 密码登录。 */
  login(email: string, password: string): Promise<LoginResult>;
  /** 使用刷新令牌换取新的会话。 */
  refresh(refreshToken: string): Promise<LoginResult>;
}

/** 已注入的认证 API 实现（默认未注入）。 */
let authApi: AuthApiPort | null = null;

/**
 * 注入认证 API 实现（供 T04 services 调用）。
 *
 * @param api 认证 API 实现，传 null 可清除。
 */
export function setAuthApi(api: AuthApiPort | null): void {
  authApi = api;
}

/** 安全获取 localStorage（规避 SSR / 隐私模式异常）。 */
function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage
      ? window.localStorage
      : null;
  } catch {
    return null;
  }
}

/** 将登录结果持久化到 localStorage。 */
function persistSession(result: LoginResult): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.setItem(TOKEN_KEY, result.token);
  storage.setItem(REFRESH_KEY, result.refreshToken);
  storage.setItem(USER_KEY, JSON.stringify(result.user));
}

/** 清除 localStorage 中的会话数据。 */
function clearSession(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  storage.removeItem(TOKEN_KEY);
  storage.removeItem(REFRESH_KEY);
  storage.removeItem(USER_KEY);
}

/** 从 localStorage 读取会话；不存在或解析失败返回 null。 */
function readSession(): {
  token: string;
  refreshToken: string;
  user: User;
} | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  const token = storage.getItem(TOKEN_KEY);
  const refreshToken = storage.getItem(REFRESH_KEY);
  const userRaw = storage.getItem(USER_KEY);
  if (!token || !refreshToken || !userRaw) return null;
  try {
    const user = JSON.parse(userRaw) as User;
    return { token, refreshToken, user };
  } catch {
    return null;
  }
}

/** 会话未注入时的统一错误提示。 */
const API_NOT_REGISTERED =
  'Auth API 尚未注册：请在 services 层（T04）调用 setAuthApi() 注入实现。';

/**
 * 认证 store 状态与动作定义。
 */
export interface AuthState {
  /** 访问令牌。 */
  token: string;
  /** 刷新令牌。 */
  refreshToken: string;
  /** 当前登录用户。 */
  user: User | null;
  /** 是否已认证（存在有效会话）。 */
  isAuthenticated: boolean;
  /** 邮箱 + 密码登录。 */
  login: (email: string, password: string) => Promise<void>;
  /** 退出登录并清除会话。 */
  logout: () => void;
  /** 更新当前用户信息（同步持久化）。 */
  setUser: (user: User) => void;
  /** 从 localStorage 恢复会话（应用启动时调用）。 */
  restoreSession: () => void;
  /** 使用刷新令牌刷新会话。 */
  refresh: () => Promise<void>;
}

/**
 * 认证 store。
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  token: '',
  refreshToken: '',
  user: null,
  isAuthenticated: false,

  login: async (email: string, password: string): Promise<void> => {
    if (!authApi) {
      throw new Error(API_NOT_REGISTERED);
    }
    const result = await authApi.login(email, password);
    set({
      token: result.token,
      refreshToken: result.refreshToken,
      user: result.user,
      isAuthenticated: true,
    });
    persistSession(result);
  },

  logout: (): void => {
    clearSession();
    set({
      token: '',
      refreshToken: '',
      user: null,
      isAuthenticated: false,
    });
  },

  setUser: (user: User): void => {
    set({ user });
    const storage = safeLocalStorage();
    if (storage) {
      storage.setItem(USER_KEY, JSON.stringify(user));
    }
  },

  restoreSession: (): void => {
    const session = readSession();
    if (session) {
      set({
        token: session.token,
        refreshToken: session.refreshToken,
        user: session.user,
        isAuthenticated: true,
      });
    }
  },

  refresh: async (): Promise<void> => {
    const { refreshToken } = get();
    if (!authApi) {
      throw new Error(API_NOT_REGISTERED);
    }
    if (!refreshToken) {
      throw new Error('无刷新令牌，无法刷新会话。');
    }
    const result = await authApi.refresh(refreshToken);
    set({
      token: result.token,
      refreshToken: result.refreshToken,
      user: result.user,
      isAuthenticated: true,
    });
    persistSession(result);
  },
}));
